import { useEffect, useRef } from 'react'

/**
 * Windowed image prefetch for the scan scrubber. Preloads a window of ±radius
 * images around the cursor into the browser cache (via `new Image()`), so
 * playing / scrubbing swaps to an already-decoded image instead of blocking on a
 * fresh CDN fetch. Images outside a slightly larger window are dropped from the
 * ref map so their decoded memory can be reclaimed and the cache never grows
 * unbounded on a long timeline.
 *
 * Web equivalent of the app's `useDeepDiveImagePrefetch` (±10 window + eviction).
 * Per web.dev prefetch guidance the hints are inserted from JS (not the initial
 * HTML) so they never compete with first paint, and prefetching follows intent —
 * it re-centres on the cursor as the provider scrubs or plays.
 */
export function useImagePrefetch(
  urls: (string | undefined)[],
  currentIdx: number,
  radius = 10
): void {
  const cache = useRef(new Map<number, HTMLImageElement>())

  useEffect(() => {
    const lo = Math.max(0, currentIdx - radius)
    const hi = Math.min(urls.length - 1, currentIdx + radius)

    for (let i = lo; i <= hi; i++) {
      const url = urls[i]
      if (url && !cache.current.has(i)) {
        const img = new Image()
        img.decoding = 'async'
        // referenced by the map so the browser keeps it decoded until evicted
        img.src = url
        cache.current.set(i, img)
      }
    }

    // Evict outside a larger window (hysteresis) so small back-and-forth scrubs
    // near the edge don't churn (re-fetch) the images just dropped. Clearing
    // `src` also aborts an in-flight download so a fast scrub across a long
    // history doesn't leave dozens of requests running.
    const keepLo = lo - radius
    const keepHi = hi + radius
    for (const [i, img] of cache.current) {
      if (i < keepLo || i > keepHi) {
        img.src = ''
        cache.current.delete(i)
      }
    }
  }, [urls, currentIdx, radius])

  useEffect(() => {
    const map = cache.current
    return () => {
      for (const img of map.values()) img.src = ''
      map.clear()
    }
  }, [])
}
